<template>
    <div>
        <modal ref="modal" :has-footer="false">
            <div class="d-block text-center" slot="title">Send Invite</div>

            <div class="animated animated-normal fadeIn" :key="key">
                <div class="form-group">
                    <label class="d-block text-center">
                        <b>Search Existing Contact</b>
                    </label>
                    <multiselect
                            id="contact_id"
                            :options="contacts"
                            :internal-search="false"
                            :loading="searchingContacts"
                            placeholder="Type to search existing contacts"
                            @search-change="findContact"
                            track-by="id"
                            label="name"
                            @select="selectModel"
                            @remove="deselectModel"
                            v-model="selectedContact"
                            :clear-on-select="true">

                        <template slot="option" slot-scope="props">
                            <div class="row">
                                <div class="col">{{ props.option.name }}</div>
                            </div>
                            <div class="row">
                                <div class="col">{{ props.option.email }}</div>
                            </div>
                            <div class="row">
                                <div class="col">{{ props.option.phone_number }}</div>
                            </div>
                        </template>
                    </multiselect>
                    <div v-if="!form.contact_id">
                        <form-title class="mt-3">Add New Contact</form-title>
                        <div class="row">
                            <div class="col">
                                <input-text
                                        label="Name"
                                        placeholder="Name"
                                        v-model="form.contact.name"
                                        :form="form"
                                        field="contact.name"
                                />
                            </div>
                            <div class="col">
                                <input-text
                                        label="Phone"
                                        placeholder="Phone"
                                        v-model="form.contact.phone_number"
                                        :form="form"
                                        field="contact.phone_number"
                                />
                            </div>
                        </div>
                        <input-text
                                label="Email"
                                placeholder="Email"
                                v-model="form.contact.email"
                                :form="form"
                                field="contact.email"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <label>Location</label>
                    <multiselect
                            :class="{'is-invalid': form.errors.has('location_id')}"
                            id="location_id"
                            :options="locations"
                            placeholder="Select a location"
                            track-by="id"
                            label="name"
                            v-model="selectedLocation"
                            @select="locationSelected"
                            @input="updateGroups"
                            @remove="deselectLocation"
                    />
                    <p class="invalid-feedback" v-if="form.errors.has('location_id')">
                        {{ form.errors.get('location_id') }}
                    </p>
                </div>

                <div class="form-group">
                    <label>Campaign</label>
                    <multiselect
                            :class="{'is-invalid': form.errors.has('campaign_id')}"
                            id="campaign_id"
                            :options="campaigns"
                            placeholder="Select a campaign"
                            track-by="id"
                            label="name"
                            :loading="loading_campaigns"
                            v-model="selectedCampaign"
                            @select="selectModel"
                            @remove="deselectModel"
                    />
                    <p class="invalid-feedback" v-if="form.errors.has('campaign_id')">
                        {{ form.errors.get('campaign_id') }}
                    </p>
                </div>

                <div class="form-group">
                    <label class="mb-0">
                        <input type="checkbox" @click="sendLater = !sendLater"/>
                        Send later
                    </label>
                </div>

                <div class="form-group" v-if="sendLater">
                    <label>Select a date and time</label>
                    <small v-if="!timezone" class="ml-auto float-right text-muted" style="padding-top: 4px">
                        Server Time:
                        <server-time
                                class="ml-1"
                                format="hh:mm:ss a"
                        />
                    </small>
                    <small v-else class="ml-auto float-right text-muted" style="padding-top: 4px">
                        Your Time:
                        <server-time
                                class="ml-1"
                                format="hh:mm:ss a"
                                :timezone="timezone"
                        />
                    </small>
                    <div class="input-group">
                        <flat-pickr
                                class="form-control"
                                :class="{'is-invalid': form.errors.has('send_at')}"
                                v-model="form.send_at"
                                :config="config"
                                placeholder="Select date"
                                name="date"
                        />
                        <div class="input-group-append">
                            <button class="btn btn-default" type="button" title="Clear" data-clear=""
                                    @click="form.send_at = null">
                                <i class="fa fa-times"><span class="sr-only" aria-hidden="true">Clear</span></i>
                            </button>
                        </div>
                        <p class="invalid-feedback" v-if="form.errors.has('send_at')">
                            {{ form.errors.get('send_at') }}
                        </p>
                    </div>
                </div>
                <div class="form-row">
                    <div class="col text-right">
                        <button-loading
                                type="button"
                                class="btn btn-primary px-4"
                                :loading="form.submitting"
                                @click="sendNow">
                            {{ !sendLater ? 'Send' : 'Schedule'}}
                        </button-loading>
                    </div>
                </div>
            </div>
        </modal>

        <modal ref="openConfirmContactLocationModal" size="lg">
            <div class="d-block text-center" slot="title">Confirm Contact</div>

            <div class="alert alert-warning mb-0">
                The selected contact is not a member of the given location.
                Are you sure you want to send an invite to this contact?
            </div>

            <span slot="footer">
                <button type="button" class="btn btn-default"
                        @click="closeModal()">
                    Cancel
                </button>
                <button type="button" class="btn btn-primary"
                        @click="sendInviteToADifferentLocation()">
                    Yes
                </button>
            </span>
        </modal>
    </div>
</template>

<script>
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import {mapState} from 'vuex';
    import _ from 'lodash';
    import axios from 'axios';

    export default {
        data() {
            return {
                key: null,
                selectedLocation: null,
                selectedContact: null,
                selectedCampaign: null,
                selectedGroup: null,
                locations: [],
                groups: [],
                campaigns: [],
                contacts: [],
                searchingContacts: false,
                form: new window.Form({contact: {}}),
                sendLater: false,
                loading_campaigns: false,
                config: {
                    wrap: true, // set wrap to true only when using 'input-group'
                    altFormat: 'M j, Y H:i',
                    altInput: true,
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    minDate: 'today'
                },
                loadContactList: false,
                timezone: null,
            };
        },

        components: {
            flatPickr
        },

        created() {
            window.Bus.$on('openInviteModal', () => {
                this.$refs.modal.open();
                this.setupForm();
                this.key = Math.random().toString(36);
                this.sendLater = false;
                this.fetchLocations();
                this.fetchGroups();
                this.fetchTimezone();
            });
        },

        computed: {
            ...mapState({
                filter: state => state.filter
            }),

            hasOneLocation() {
                return this.locations.length === 1;
            },

            hasOnecampaign() {
                return this.campaigns.length === 1;
            }
        },

        watch: {
            locations() {
                if (this.hasOneLocation) {
                    this.selectedLocation = this.locations[0];
                    this.locationSelected(this.locations[0]);
                }
            },

            campaigns() {
                if (this.hasOnecampaign) {
                    this.selectedCampaign = this.campaigns[0];
                    this.selectModel(this.selectedCampaign, 'campaign_id');
                }
            }
        },

        methods: {
            setupForm() {
                this.selectedLocation = null;
                this.selectedGroup = null;
                this.selectedCampaign = null;
                this.selectedContact = null;

                this.form = new window.Form({
                    location_id: undefined,
                    campaign_id: undefined,
                    contact_id: undefined,
                    contact: {
                        name: undefined,
                        phone_number: undefined,
                        email: undefined,
                        locations: {},
                        groups: {}
                    },
                    send_at: null,
                    timezone: null
                });
            },

            fetchTimezone() {
                return this.$http.get(this.$laroute.route('api.teams.current-team-timezone')).then(({data}) => {
                    this.timezone = data;
                });
            },

            fetchLocations() {
                return this.$http.get(this.$laroute.route('api.locations.index')).then(({data}) => (this.locations = data.data));
            },

            fetchGroups() {
                return this.$http.get('/groups').then(({data}) => (this.groups = data.data));
            },

            updateGroups() {
                let params = {locations: [this.selectedLocation.id]};
                return this.$http.get('/groups', {params}).then(({data}) => {
                    this.groups = data.data;
                });
            },

            fetchCampaigns(location_id) {
                this.loading_campaigns = true;
                this.form.campaign_id = null;
                this.campaigns = [];
                this.selectedCampaign = null;

                return this.$http
                    .get(
                        this.$laroute.route('api.locations.campaigns', {
                            location: location_id
                        })
                    )
                    .then(({data}) => {
                        this.campaigns = data.data;
                        this.loading_campaigns = false;
                    });
            },

            locationSelected(location) {
                this.form.location_id = location.id;
                this.form.contact.locations = {location};
                this.fetchCampaigns(location.id);
            },

            deselectLocation() {
                this.groups = [];
                this.form.location_id = null;
                this.form.campaign_id = null;
                this.selectedCampaign = null;
            },

            groupSelected(group) {
                this.form.contact.groups = {group};
            },

            findContact: _.debounce(function (query) {
                this.searchingContacts = true;
                const params = {
                    search: query
                };
                this.$http.get(this.$laroute.route('api.contacts.select'), {params}).then(({data}) => {
                    this.contacts = data.data;
                    this.searchingContacts = false;
                });
            }, 500),

            selectModel(model, option) {
                this.form[option] = model.id;
                if (option === 'contact_id') {
                    this.form.contact = {};
                }
            },

            deselectModel(model, option) {
                this.form[option] = undefined;
            },

            confirmSendInviteToADifferentLocation() {
                this.$refs.modal.close();
                this.$refs.openConfirmContactLocationModal.open();
            },

            sendInviteToADifferentLocation() {
                this.$refs.openConfirmContactLocationModal.close();
                this.sendNow(false);
                this.loadContactList = true;
            },

            async sendNow(checkLocation = true) {
                this.form.scheduled = this.sendLater;
                if (this.timezone) {
                    this.form.timezone = this.timezone;
                }
                if (checkLocation && this.form.contact_id && this.form.location_id) {
                    let uri = this.$laroute.route('api.contact.in_location', {
                        contact: this.form.contact_id,
                        location: this.form.location_id
                    });
                    let {data} = await axios.get(uri);
                    if (!data.in_location) {
                        this.confirmSendInviteToADifferentLocation();
                        return;
                    }
                }

                try {
                    const response = await this.form.post(this.$laroute.route('api.invites.store'));
                    this.selectedLocation = null;
                    this.selectedCampaign = null;
                    this.selectedContact = null;
                    this.selectedGroup = null;
                    this.$alert({type: 'success', title: 'Invite sent!'});
                    this.$refs.modal.close();

                    if (this.loadContactList) {
                        this.loadContactList = false;
                        window.Bus.$emit('LoadContactList');
                    }
                } catch ({data}) {
                    if (data.message.period) {
                        swal.fire({
                            title: data.message.period,
                            type: 'error',
                            showCancelButton: true,
                            confirmButtonText: 'Override'
                        }).then(override => {
                            if (override.value) {
                                this.form.override = true;
                                this.sendNow(false);
                            }
                        });
                    }
                }
            },

            addContactLocation() {
                const params = {
                    contact: this.selectedContact.id,
                    location: this.selectedLocation.id,
                    group: this.selectedGroup.id
                };
                this.$http.post(this.$laroute.route('api.invites.store-contact-location'), params).then(() => {
                    swal.fire({
                        title: 'Attach contact it to that location',
                        text: 'Contact successfully attached!',
                        type: 'success'
                    }).then(() => {
                        this.closeModal();
                    });
                });
            },

            openModal() {
                this.$refs.modal.close();
                this.$refs.openConfirmContactLocationModal.open();
            },

            closeModal() {
                this.$refs.openConfirmContactLocationModal.close();
                this.$refs.modal.open();
            }
        }
    };
</script>
